import { BOX_TRUCK_EQUIPMENT_LABELS, BoxTruckDriverAvailabilityStatus, BoxTruckVerificationStatus, CanonicalLoad } from '@numeo/types-core'
import type { CanonicalLoadInput } from '@numeo/types-core'

/** Small display formatters for the Box Truck Loads board. All guard against null / Invalid Date. */

/**
 * The board's date/number formatters, built ONCE at module load.
 *
 * `date.toLocaleString(locale, options)` is specified as "construct an Intl.DateTimeFormat, then format with
 * it" — so every call pays for a fresh formatter, and constructing one is the expensive half by a wide margin
 * (measured on this machine: 70.9 µs per `toLocaleString` call against 2.25 µs per `.format()` on a reused
 * formatter — 31x). The Received column alone spends up to four of them per row (the cell, plus the two
 * clocks in its tooltip), so a 40-row screen was ~11 ms of formatter construction on EVERY render of the
 * table — two thirds of a 60 fps frame, for a value that never changes.
 *
 * Reusing the instances is output-identical: the options and locale are the same, and the format call reads
 * the same system timezone. The one difference is that a hoisted formatter resolves that timezone once, so a
 * tab left open ACROSS a system timezone change keeps the old zone until reload. That is the trade, and it is
 * the right one for a dispatch board that is reloaded daily.
 */
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' })
const MONTH_DAY_FMT = new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit' })
const DATE_TIME_FMT = new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const INTEGER_FMT = new Intl.NumberFormat('en-US')

/** Map a driver's Box Truck verification status to a ds2 Badge type + label. Shared by the Driver
 *  Verifications queue and My Drivers list so the mapping stays in one place. */
export const boxTruckStatusBadge = (
    status: BoxTruckVerificationStatus | undefined,
    /** False when no account stands behind the row — see `BoxTruckVerification.appAccountLinked`. */
    appAccountLinked: boolean = true
): { type: 'success' | 'warning' | 'default'; label: string } => {
    // A driver nobody has signed in as has no verification status to report. Showing "Unverified" would read
    // as "they applied and we have not reviewed them", when the truth is that they have never been asked —
    // and it is also the answer to "why is this driver not on the map".
    if (!appAccountLinked) return { type: 'default', label: 'No app account' }
    if (status === 'active') return { type: 'success', label: 'Active' }
    if (status === 'pending') return { type: 'warning', label: 'Unverified' }
    return { type: 'default', label: 'Inactive' }
}

/** Every dispatch-availability status in display order, with its ds2 Badge type + label — the single source
 *  for the My Drivers editor options, the badge, and the Nearby Drivers read-only badge. */
export const BOX_TRUCK_AVAILABILITY_OPTIONS: { value: BoxTruckDriverAvailabilityStatus; label: string; type: 'success' | 'warning' | 'danger' }[] = [
    { value: 'available', label: 'Available', type: 'success' },
    { value: 'booked', label: 'Booked', type: 'warning' },
    { value: 'unavailable', label: 'Unavailable', type: 'danger' },
]

/** Map a driver's dispatch availability to a ds2 Badge type + label, or null when unset. */
/** Equipment values the driver-list filters offer. Union of the admin registration picker's labels and
 *  the values MOBILE registration writes ('Box Truck' — the mobile options list has it where the admin
 *  labels have 'Tractor'), so no stored value is unreachable by filter. Free-text "Other" values remain
 *  findable via search only. */
export const BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS = [...new Set([...BOX_TRUCK_EQUIPMENT_LABELS, 'Box Truck'])]

/** Built-in lead sources always offered (Add-driver modal + My Drivers Source filter); a team's custom
 *  sources are appended to these. */
export const BOX_TRUCK_DEFAULT_LEAD_SOURCES = ['Numeo', 'Facebook', 'Indeed']

export const boxTruckAvailabilityBadge = (
    status: BoxTruckDriverAvailabilityStatus | undefined | null
): { type: 'success' | 'warning' | 'danger'; label: string } | null => BOX_TRUCK_AVAILABILITY_OPTIONS.find((o) => o.value === status) ?? null

/** Parse an ISO string (or pass a Date through) to a valid Date, or null for empty / Invalid Date. The one
 *  ISO→Date guard for the box-truck feature — reused by the tab + filter persistence readers. */
export const toDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) return null
    const d = value instanceof Date ? value : new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

/** "Dallas, TX" — trims gracefully when one side is missing. */
export const cityLabel = (city: string | null | undefined, state: string | null | undefined): string => {
    const c = (city ?? '').trim()
    const s = (state ?? '').trim()
    if (c && s) return `${c}, ${s}`
    return c || s || '—'
}

export type LoadLaneFields = Pick<CanonicalLoadInput, 'pickupCity' | 'pickupStateProv' | 'deliveryCity' | 'deliveryStateProv'>

/** "Dallas, TX → Miami, FL" for compact load displays. No label means an older event did not carry
 *  the load, or the source supplied neither endpoint; callers can then preserve their existing fallback. */
export const loadLaneLabel = (load: LoadLaneFields | null | undefined): string | null => {
    if (!load) return null
    const origin = cityLabel(load.pickupCity, load.pickupStateProv)
    const destination = cityLabel(load.deliveryCity, load.deliveryStateProv)
    return origin === '—' && destination === '—' ? null : `${origin} → ${destination}`
}

/** "Dallas, TX" for optional profile / GPS locality fields; empty when neither is known. */
export const fmtProfileLocation = (city: string | null | undefined, state: string | null | undefined): string =>
    [city, state]
        .map((value) => value?.trim())
        .filter(Boolean)
        .join(', ')

/** Google Maps search link for a GPS coordinate pair. */
export const googleMapsUrl = (lat: number, lng: number): string => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`

/** "10:36 AM" */
export const fmtTime = (value: Date | string | null | undefined): string => {
    const d = toDate(value)
    return d ? TIME_FMT.format(d) : '—'
}

/**
 * A load's posted time in the operator's own timezone: "10:36 AM" for today, "07/22 10:36 AM" otherwise.
 *
 * No zone label. It was added so a dispatcher comparing this board against atrek — which shows Sylectus's
 * own poster-zone string verbatim ("08:41 CEN") — could see that 09:41 and 08:41 are the same instant rather
 * than a broken clock. But every row on the board is in the reader's one zone, so the label repeated the same
 * few characters down the whole column to answer a question asked about one load at a time. That answer now
 * lives in the cell's hover ({@link postedAtTitle}), which gives the source's own wording too.
 *
 * The date only appears when it is needed. This board carries loads posted up to a day back, so a bare
 * clock time would be ambiguous on anything but today's — and printing the date on every row would spend
 * width on a value that is the same for most of them.
 */
export const fmtPostedAt = (value: Date | string | null | undefined): string => {
    const d = toDate(value)
    if (!d) return '—'
    const time = TIME_FMT.format(d)
    const now = new Date()
    const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
    return sameDay ? time : `${MONTH_DAY_FMT.format(d)} ${time}`
}

/**
 * What the source itself said, for the Posted cell's tooltip — Sylectus's own "Posted" line, kept verbatim
 * at ingest precisely so it can be shown next to our converted rendering. A dispatcher checking us against
 * atrek can hover and see the same string atrek shows.
 *
 * Nothing to add for a load without one (DAT and the rest): the cell already carries the full local
 * date-time, and inventing a tooltip that just repeats it would be noise.
 */
export const postedAtTitle = (load: Pick<CanonicalLoad, 'createdAt' | 'details'>): string | undefined => {
    const raw = load.details?.sylectusPostedText
    const shown = fmtDateTime(load.createdAt)
    if (typeof raw !== 'string' || !raw.trim()) return shown === '—' ? undefined : shown
    return `Source: ${raw.trim()} · your time: ${shown}`
}

/**
 * When the load reached US — not when the broker says it was posted.
 *
 * Those are usually the same, and where they are the posted time is the better read. But a chunk of the
 * Sylectus feed states a posted time of exactly "12:00 EST" — the same value from unrelated brokers, on
 * emails that arrived hours apart, and sometimes HOURS AFTER the load was already in our hands (measured:
 * two loads stamped 12:00 EST that we had ingested at 06:03 and 06:26 EST the same morning). A save time
 * cannot precede the event it records, so that value is a placeholder, not a clock.
 *
 * `sylectusIngestedAt` is when the row was actually written, so it wins where it exists. A load with neither
 * that nor a plausible posted time returns null: it tells us nothing about arrival, and must not be allowed
 * to squat on the top row of a board whose whole question is "what just came in".
 */
/** The recorded write time, or null when the row carries none (every non-Sylectus source). */
const ingestTime = (load: Pick<CanonicalLoad, 'details'>): number | null => {
    const ingested = load.details?.sylectusIngestedAt
    if (typeof ingested !== 'string') return null
    const parsed = Date.parse(ingested)
    return Number.isNaN(parsed) ? null : parsed
}

/** How far ahead of this browser's clock a posted time may sit and still count as real. A stream row's `createdAt` is the
 *  server's first-seen time, so a minutes-ahead value means this machine's clock runs behind, not that the time is a
 *  placeholder — refusing it sent the newest loads to the bottom of the board until the local clock caught up. */
const CLOCK_SKEW_TOLERANCE_MS = 2 * 60 * 60 * 1000

export const arrivedAt = (load: Pick<CanonicalLoad, 'createdAt' | 'details'>): number | null => {
    const ingested = ingestTime(load)
    if (ingested != null) return ingested
    if (!load.createdAt) return null
    const posted = new Date(load.createdAt).getTime()
    if (Number.isNaN(posted)) return null
    return posted > Date.now() + CLOCK_SKEW_TOLERANCE_MS ? null : posted
}

/** The Received cell: the same clock-time format the board already uses, on the arrival value. */
export const fmtReceivedAt = (load: Pick<CanonicalLoad, 'createdAt' | 'details'>): string => {
    const at = arrivedAt(load)
    return at == null ? '—' : fmtPostedAt(new Date(at))
}

/**
 * The Received tooltip carries both clocks WHEN THERE ARE TWO — the column is what the board is ordered by,
 * the broker's claim is what the operator will hear quoted on the phone, and hiding that they disagree is
 * what made the board look mis-sorted.
 *
 * When there is only one — no recorded write time, so the cell is falling back to the posted time (DAT and
 * every non-Sylectus source) — it says exactly that instead of printing the value twice under two headings.
 * The duplicate was worse than silence: it implied two independent clocks that happened to agree, on the very
 * rows where we have no arrival time at all.
 */
export const receivedAtTitle = (load: Pick<CanonicalLoad, 'createdAt' | 'details'>): string | undefined => {
    const claimed = postedAtTitle(load)
    const ingested = ingestTime(load)
    // No write time recorded: whatever the cell shows IS the posted time, so name it that and stop.
    if (ingested == null) return claimed ? `Broker posted — ${claimed}` : undefined
    const received = `Received: ${fmtDateTime(new Date(ingested))}`
    return claimed ? `${received} · Broker posted — ${claimed}` : received
}

/** "07/22/2026 10:36 AM" */
export const fmtDateTime = (value: Date | string | null | undefined): string => {
    const d = toDate(value)
    if (!d) return '—'
    return DATE_TIME_FMT.format(d)
}

/**
 * Formatted datetime when the value parses as a date; otherwise the raw text verbatim — Sylectus
 * pickup/delivery can be free text like "ASAP" or "Deliver Direct" instead of a date. Falls back to "—".
 */
export const fmtDateOrText = (value: Date | string | null | undefined): string => {
    const d = toDate(value)
    if (d) return DATE_TIME_FMT.format(d)
    if (typeof value === 'string' && value.trim()) return value.trim()
    return '—'
}

/** "283 mi" */
export const fmtMiles = (miles: number | null | undefined): string => {
    if (miles == null || !Number.isFinite(miles)) return '—'
    return `${INTEGER_FMT.format(Math.round(miles))} mi`
}

/** "40 lbs" */
export const fmtWeight = (lbs: number | null | undefined): string => {
    if (lbs == null || !Number.isFinite(lbs)) return '—'
    return `${INTEGER_FMT.format(Math.round(lbs))} lbs`
}

/** Expiry instant, e.g. "07/22/2026 06:30 PM". */
export const fmtExpires = (iso: string | null | undefined): string => fmtDateTime(iso)

/**
 * Did the server send this value masked?
 *
 * An owner's account and routing numbers come back in full to a caller who may WRITE them and as `••••8306`
 * to everyone else, so a form can hold either. The question matters on the write side: saving a form that
 * was filled from a masked read would put `••••8306` where the real number was. Defined once so the two
 * deposit forms cannot disagree about what a mask looks like.
 */
export const isMaskedValue = (value: unknown): boolean => typeof value === 'string' && value.startsWith('••••')
